import { useState } from "react";
import Layout from "@/components/layout/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, Plus, Pencil, Trash2, Loader2, RefreshCw } from "lucide-react";
import { useAgencies, useCreateAgency, useUpdateAgency, useDeleteAgency, useSystemSettings, useUpdateSystemSettings } from "@/lib/api";
import { useAuth, useUpdateUserSettings } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { FieldError } from "@/components/FieldError";
import type { Agency } from "@shared/schema";

const emptyAgencyForm = {
  name: "",
  address: "",
  phone: "",
  email: "",
  authorizedRepresentative: "",
};

type AgencyForm = typeof emptyAgencyForm;

function getAgencyErrors(form: AgencyForm) {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
    errors.email = "Invalid email address";
  }
  return errors;
}

function hasErrors(errors: Record<string, string>) {
  return Object.keys(errors).length > 0;
}

export default function Settings() {
  const { data: agencies, isLoading: agenciesLoading } = useAgencies();
  const { user } = useAuth();
  const updateSettings = useUpdateUserSettings();
  const { toast } = useToast();

  const { data: systemSettings, isLoading: settingsLoading } = useSystemSettings();
  const updateSystemSettings = useUpdateSystemSettings();

  const createAgency = useCreateAgency();
  const updateAgency = useUpdateAgency();
  const deleteAgency = useDeleteAgency();

  // Dialog state
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingAgency, setEditingAgency] = useState<Agency | null>(null);
  const [deletingAgency, setDeletingAgency] = useState<Agency | null>(null);
  const [agencyForm, setAgencyForm] = useState<AgencyForm>(emptyAgencyForm);
  const [tried, setTried] = useState(false);

  const errors = getAgencyErrors(agencyForm);
  const errClass = (field: string) =>
    tried && errors[field] ? "border-destructive" : "";

  const openAddDialog = () => {
    setAgencyForm(emptyAgencyForm);
    setTried(false);
    setIsAddDialogOpen(true);
  };

  const openEditDialog = (agency: Agency) => {
    setAgencyForm({
      name: agency.name,
      address: agency.address || "",
      phone: agency.phone || "",
      email: agency.email || "",
      authorizedRepresentative: agency.authorizedRepresentative || "",
    });
    setTried(false);
    setEditingAgency(agency);
  };

  const handleCreateAgency = async () => {
    setTried(true);
    if (hasErrors(errors)) return;

    const name = agencyForm.name.trim();
    const autoCode = name.slice(0, 4).toUpperCase() + "-" + Math.random().toString(36).slice(2, 6).toUpperCase();

    try {
      await createAgency.mutateAsync({
        id: `AGC-${Date.now().toString(36).toUpperCase()}`,
        name,
        code: autoCode,
        address: agencyForm.address.trim() || null,
        phone: agencyForm.phone.trim() || null,
        email: agencyForm.email.trim() || null,
        authorizedRepresentative: agencyForm.authorizedRepresentative.trim() || null,
      });
      toast({ title: "Success", description: "Agency created successfully" });
      setIsAddDialogOpen(false);
      setAgencyForm(emptyAgencyForm);
      setTried(false);
    } catch {
      toast({
        title: "Error",
        description: "Failed to create agency",
        variant: "destructive",
      });
    }
  };

  const handleUpdateAgency = async () => {
    if (!editingAgency) return;
    setTried(true);
    if (hasErrors(errors)) return;

    try {
      await updateAgency.mutateAsync({
        id: editingAgency.id,
        data: {
          name: agencyForm.name.trim(),
          address: agencyForm.address.trim() || null,
          phone: agencyForm.phone.trim() || null,
          email: agencyForm.email.trim() || null,
          authorizedRepresentative: agencyForm.authorizedRepresentative.trim() || null,
        },
      });
      toast({ title: "Success", description: "Agency updated successfully" });
      setEditingAgency(null);
      setAgencyForm(emptyAgencyForm);
      setTried(false);
    } catch {
      toast({
        title: "Error",
        description: "Failed to update agency",
        variant: "destructive",
      });
    }
  };

  const handleDeleteAgency = async () => {
    if (!deletingAgency) return;

    try {
      await deleteAgency.mutateAsync(deletingAgency.id);
      toast({ title: "Success", description: "Agency deleted successfully" });
      setDeletingAgency(null);
    } catch (error) {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to delete agency",
        variant: "destructive",
      });
    }
  };

  const updateField = (field: keyof AgencyForm, value: string) => {
    setAgencyForm((prev) => ({ ...prev, [field]: value }));
  };

  const agencyFormFields = (
    <div className="grid gap-4 py-4">
      <div className="grid gap-2">
        <Label htmlFor="agency-name">Name *</Label>
        <Input
          id="agency-name"
          placeholder="Agency name"
          value={agencyForm.name}
          onChange={(e) => updateField("name", e.target.value)}
          className={errClass("name")}
          maxLength={200}
        />
        <FieldError show={tried && !!errors.name} message={errors.name || ""} />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="agency-email">Email</Label>
        <Input
          id="agency-email"
          type="email"
          placeholder="agency@example.com"
          value={agencyForm.email}
          onChange={(e) => updateField("email", e.target.value)}
          className={errClass("email")}
          maxLength={200}
        />
        <FieldError show={tried && !!errors.email} message={errors.email || ""} />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="agency-phone">Phone</Label>
        <Input
          id="agency-phone"
          type="tel"
          placeholder="(555) 123-4567"
          value={agencyForm.phone}
          onChange={(e) => updateField("phone", e.target.value)}
          maxLength={20}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="agency-address">Address</Label>
        <Input
          id="agency-address"
          placeholder="123 Main St, City, State"
          value={agencyForm.address}
          onChange={(e) => updateField("address", e.target.value)}
          maxLength={500}
        />
      </div>
      <div className="grid gap-2">
        <Label htmlFor="agency-authorized-rep">Authorized Representative</Label>
        <Input
          id="agency-authorized-rep"
          placeholder="Full name (used to sign COIs)"
          value={agencyForm.authorizedRepresentative}
          onChange={(e) => updateField("authorizedRepresentative", e.target.value)}
          maxLength={200}
        />
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold font-heading text-foreground tracking-tight">Settings</h1>
            <p className="text-muted-foreground mt-1">
              Manage agencies, users, and view system activity.
            </p>
          </div>
        </div>

        {/* Personal Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Personal</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="crm-access"
                    checked={user?.crmAccess ?? false}
                    onCheckedChange={(checked) => {
                      updateSettings.mutate(
                        { crmAccess: checked === true },
                        {
                          onSuccess: () => toast({ title: "Settings saved" }),
                          onError: () => toast({ title: "Error", description: "Failed to save settings", variant: "destructive" }),
                        }
                      );
                    }}
                    disabled={updateSettings.isPending}
                  />
                  <Label htmlFor="crm-access" className="cursor-pointer">
                    CRM access
                  </Label>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Data Sync Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Data Sync</h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-muted-foreground" />
                  <Label htmlFor="sync-interval" className="font-medium">Auto-sync interval</Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  How often policies and insureds are automatically synced from the Data Center.
                </p>
                {settingsLoading ? (
                  <div className="h-10 w-full bg-muted animate-pulse rounded" />
                ) : (
                  <Select
                    value={String(systemSettings?.syncIntervalMinutes ?? 15)}
                    onValueChange={(value) => {
                      updateSystemSettings.mutate(
                        { syncIntervalMinutes: Number(value) },
                        {
                          onSuccess: () => toast({ title: "Settings saved", description: `Sync interval set to ${value} minutes` }),
                          onError: () => toast({ title: "Error", description: "Failed to update sync interval", variant: "destructive" }),
                        }
                      );
                    }}
                    disabled={updateSystemSettings.isPending}
                  >
                    <SelectTrigger id="sync-interval" className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">Every 5 minutes</SelectItem>
                      <SelectItem value="10">Every 10 minutes</SelectItem>
                      <SelectItem value="15">Every 15 minutes</SelectItem>
                      <SelectItem value="30">Every 30 minutes</SelectItem>
                      <SelectItem value="60">Every 60 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Agencies</h2>
              <Button className="gap-2 cursor-pointer" onClick={openAddDialog}>
                <Plus className="h-4 w-4" />
                Add Agency
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {agenciesLoading ? (
                [1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="h-24 bg-muted animate-pulse rounded" />
                    </CardContent>
                  </Card>
                ))
              ) : (
                agencies?.map((agency) => (
                  <Card key={agency.id} className="h-full flex flex-col hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{agency.name}</CardTitle>
                          <CardDescription className="font-mono text-xs">{agency.code}</CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex flex-col flex-1 pt-2">
                      <div className="text-sm text-muted-foreground space-y-1">
                        {agency.email && <p>{agency.email}</p>}
                        {agency.phone && <p>{agency.phone}</p>}
                        {agency.address && (
                          <p className="text-xs truncate">{agency.address}</p>
                        )}
                        {agency.authorizedRepresentative && (
                          <p className="text-xs">Rep: {agency.authorizedRepresentative}</p>
                        )}
                      </div>
                      <div className="flex gap-2 mt-auto pt-3 border-t">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 cursor-pointer"
                          onClick={() => openEditDialog(agency)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-destructive hover:text-destructive cursor-pointer"
                          onClick={() => setDeletingAgency(agency)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
        </div>
      </div>

      {/* Add Agency Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Agency</DialogTitle>
            <DialogDescription>
              Create a new insurance agency. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          {agencyFormFields}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateAgency}
              disabled={createAgency.isPending}
              className="cursor-pointer"
            >
              {createAgency.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create Agency"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Agency Dialog */}
      <Dialog open={!!editingAgency} onOpenChange={(open) => { if (!open) setEditingAgency(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Agency</DialogTitle>
            <DialogDescription>
              Update agency details. Fields marked with * are required.
            </DialogDescription>
          </DialogHeader>
          {agencyFormFields}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditingAgency(null)}
              className="cursor-pointer"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateAgency}
              disabled={updateAgency.isPending}
              className="cursor-pointer"
            >
              {updateAgency.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Agency Confirmation */}
      <AlertDialog open={!!deletingAgency} onOpenChange={(open) => { if (!open) setDeletingAgency(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Agency</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deletingAgency?.name}</strong>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={buttonVariants({ variant: "destructive" })}
              disabled={deleteAgency.isPending}
              onClick={handleDeleteAgency}
            >
              {deleteAgency.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
